import Navbar from "components/navbar/Navbar";
import products from "db/products.json";
import { useState } from "react";
import DetailProduct from "components/detail-product/DetailProduct";
import "./Catalogue.scss";
import SearchBar from "./search-bar/SearchBar";
import CatalogueSelect from "./catalogue-select/CatalogueSelect";
import useComponentVisible from "utils/useComponentVisible";
import Icon from "@mdi/react";
import { mdiMagnify, mdiArrowUp, mdiArrowDown, mdiArrowRight } from "@mdi/js";

const Catalogue = ({ history }) => {
  const { ref, isComponentVisible, open, close } = useComponentVisible(false);
  const [query, setQuery] = useState("");
  const [series, setSeries] = useState(null);
  const [lines, setLines] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const extractSingleValues = (type) =>
    products
      .map((element) => element[type])
      .filter((item, i, ar) => ar.indexOf(item) === i)
      .sort((a, b) => a - b);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortProducts = (products) => {
    if (!sortColumn) return products;

    return [...products].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
  };

  const filteredProducts = products
    .filter((product) => filterProducts(product.series, series))
    .filter((product) => filterProducts(product.total_lines, lines))
    .filter((product) => searchProducts(product, query));

  const sortedProducts = sortProducts(filteredProducts);

  const activeFiltersCount =
    (series !== null ? 1 : 0) + (lines !== null ? 1 : 0) + (query ? 1 : 0);

  return (
    <div
      className={
        "catalogue-view" + (history.action === "PUSH" ? " animate" : "")
      }
    >
      {isComponentVisible && (
        <DetailProduct
          propRef={ref}
          product={isComponentVisible}
          close={close}
          catalogue={true}
        />
      )}

      <Navbar title="Configured models available" />

      <div className="catalogue-list">
        <div className="container section">
          <div className="catalogue__header">
            <div className="catalogue__filters">
              <SearchBar
                value={query}
                onChange={setQuery}
                placeholder="Search Product..."
              />

              <CatalogueSelect
                value={series}
                placeholder="Series"
                onChange={setSeries}
                options={extractSingleValues("series")}
              />

              <CatalogueSelect
                value={lines}
                placeholder="Lines"
                onChange={setLines}
                options={extractSingleValues("total_lines")}
              />

              <div className="results-counter">
                <span className="results-counter__number">
                  {sortedProducts.length}
                </span>
                <span className="results-counter__label">
                  result{sortedProducts.length !== 1 ? "s" : ""}
                </span>
                {activeFiltersCount > 0 && (
                  <span className="filters-badge">
                    {activeFiltersCount} filter
                    {activeFiltersCount !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
          </div>

          {sortedProducts.length > 0 ? (
            <table className="catalogue-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort("code")} className="sortable">
                    <div className="th-content">
                      Code
                      {sortColumn === "code" && (
                        <Icon
                          path={
                            sortDirection === "asc" ? mdiArrowUp : mdiArrowDown
                          }
                          size={0.7}
                        />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort("name")} className="sortable">
                    <div className="th-content">
                      Name
                      {sortColumn === "name" && (
                        <Icon
                          path={
                            sortDirection === "asc" ? mdiArrowUp : mdiArrowDown
                          }
                          size={0.7}
                        />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("total_lines")}
                    className="sortable"
                  >
                    <div className="th-content">
                      Total Lines
                      {sortColumn === "total_lines" && (
                        <Icon
                          path={
                            sortDirection === "asc" ? mdiArrowUp : mdiArrowDown
                          }
                          size={0.7}
                        />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort("series")} className="sortable">
                    <div className="th-content">
                      Series
                      {sortColumn === "series" && (
                        <Icon
                          path={
                            sortDirection === "asc" ? mdiArrowUp : mdiArrowDown
                          }
                          size={0.7}
                        />
                      )}
                    </div>
                  </th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {sortedProducts.map((product, i) => (
                  <tr key={i}>
                    <td data-label="Code">{product.code}</td>
                    <td data-label="Name">{product.name}</td>
                    <td data-label="Total Lines">{product.total_lines}</td>
                    <td data-label="Series">{product.series}</td>
                    <td data-label="">
                      <button
                        className="btn table"
                        onClick={() => open(product)}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <Icon path={mdiMagnify} size={3} />
              <h3>No results found</h3>
              <p>Try adjusting your filters or search query</p>
              {activeFiltersCount > 0 && (
                <button
                  className="btn table primary"
                  onClick={() => {
                    setQuery("");
                    setSeries(null);
                    setLines(null);
                  }}
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}

          {/* Back to Configurator Button */}
          <div className="catalogue__footer">
            <button
              className="btn table primary"
              onClick={() => history.push("/configurator")}
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
            >
              Back to Configurator
              <Icon path={mdiArrowRight} size={0.8} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Catalogue;

const searchProducts = (product, query) =>
  Object.values(product).some((value) => {
    return value.toString().toLowerCase().includes(query.toLowerCase());
  });

const filterProducts = (element, value) =>
  value === null ? element : element === value;
